import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Pencil, Trash2, Plus, Upload } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const [cards, setCards] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCard, setEditingCard] = useState(null);
  const [editingLocation, setEditingLocation] = useState(null);
  const [isCardDialogOpen, setIsCardDialogOpen] = useState(false);
  const [isLocationDialogOpen, setIsLocationDialogOpen] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [cardsRes, locationsRes] = await Promise.all([
        axios.get(`${API}/cards`),
        axios.get(`${API}/locations`)
      ]);
      setCards(cardsRes.data);
      setLocations(locationsRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch data',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteCard = async (cardId) => {
    if (window.confirm('Are you sure you want to delete this card?')) {
      try {
        await axios.delete(`${API}/cards/${cardId}`);
        toast({
          title: 'Success',
          description: 'Card deleted successfully'
        });
        fetchData();
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Failed to delete card',
          variant: 'destructive'
        });
      }
    }
  };

  const handleDeleteLocation = async (locationId) => {
    if (window.confirm('Are you sure you want to delete this location?')) {
      try {
        await axios.delete(`${API}/locations/${locationId}`);
        toast({
          title: 'Success',
          description: 'Location deleted successfully'
        });
        fetchData();
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Failed to delete location',
          variant: 'destructive'
        });
      }
    }
  };

  const openCardDialog = (card = null) => {
    setEditingCard(card);
    setIsCardDialogOpen(true);
  };

  const openLocationDialog = (location = null) => {
    setEditingLocation(location);
    setIsLocationDialogOpen(true);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return (
    <div className="container mx-auto p-6" data-testid="admin-panel">
      <h1 className="text-4xl font-bold mb-8">Admin Panel</h1>
      
      <Tabs defaultValue="cards" className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="cards" data-testid="cards-tab">Cards ({cards.length})</TabsTrigger>
          <TabsTrigger value="locations" data-testid="locations-tab">Locations ({locations.length})</TabsTrigger>
        </TabsList>
        
        <TabsContent value="cards" data-testid="cards-content">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Manage Cards</CardTitle>
                  <CardDescription>Add, edit, or remove cards from the game</CardDescription>
                </div>
                <Button onClick={() => openCardDialog()} data-testid="add-card-btn">
                  <Plus className="mr-2 h-4 w-4" /> Add Card
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Cost</TableHead>
                      <TableHead>Power</TableHead>
                      <TableHead>Ability Type</TableHead>
                      <TableHead>Movie Source</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cards.map((card) => (
                      <TableRow key={card.id} data-testid={`card-row-${card.id}`}>
                        <TableCell className="font-medium">{card.name}</TableCell>
                        <TableCell>{card.cost}</TableCell>
                        <TableCell>{card.power}</TableCell>
                        <TableCell>{card.ability_type}</TableCell>
                        <TableCell>{card.movie_source}</TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={() => openCardDialog(card)} data-testid={`edit-card-${card.id}`}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="destructive" size="sm" onClick={() => handleDeleteCard(card.id)} data-testid={`delete-card-${card.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="locations" data-testid="locations-content">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Manage Locations</CardTitle>
                  <CardDescription>Add, edit, or remove locations from the game</CardDescription>
                </div>
                <Button onClick={() => openLocationDialog()} data-testid="add-location-btn">
                  <Plus className="mr-2 h-4 w-4" /> Add Location
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Effect Type</TableHead>
                      <TableHead>Effect Description</TableHead>
                      <TableHead>Effect Value</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {locations.map((location) => (
                      <TableRow key={location.id} data-testid={`location-row-${location.id}`}>
                        <TableCell className="font-medium">{location.name}</TableCell>
                        <TableCell>{location.effect_type}</TableCell>
                        <TableCell>{location.effect_description}</TableCell>
                        <TableCell>{location.effect_value ?? 'N/A'}</TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={() => openLocationDialog(location)} data-testid={`edit-location-${location.id}`}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="destructive" size="sm" onClick={() => handleDeleteLocation(location.id)} data-testid={`delete-location-${location.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <CardFormDialog
        open={isCardDialogOpen}
        onOpenChange={setIsCardDialogOpen}
        card={editingCard}
        onSuccess={fetchData}
      />

      <LocationFormDialog
        open={isLocationDialogOpen}
        onOpenChange={setIsLocationDialogOpen}
        location={editingLocation}
        onSuccess={fetchData}
      />
    </div>
  );
};

// Card Form Dialog Component
const CardFormDialog = ({ open, onOpenChange, card, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    cost: 0,
    power: 0,
    ability_type: 'none',
    ability_description: '',
    movie_source: '',
    image_url: ''
  });
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (card) {
      setFormData(card);
    } else {
      setFormData({
        name: '',
        cost: 0,
        power: 0,
        ability_type: 'none',
        ability_description: '',
        movie_source: '',
        image_url: ''
      });
    }
  }, [card, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (card) {
        await axios.put(`${API}/cards/${card.id}`, formData);
        toast({ title: 'Success', description: 'Card updated successfully' });
      } else {
        await axios.post(`${API}/cards`, formData);
        toast({ title: 'Success', description: 'Card created successfully' });
      }
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save card', variant: 'destructive' });
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !card) return;

    setUploading(true);
    try {
      const formDataImg = new FormData();
      formDataImg.append('file', file);
      const response = await axios.post(`${API}/admin/upload-image/${card.id}`, formDataImg);
      setFormData({ ...formData, image_url: response.data.image_url });
      toast({ title: 'Success', description: 'Image uploaded successfully' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to upload image', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="card-form-dialog">
        <DialogHeader>
          <DialogTitle>{card ? 'Edit Card' : 'Create Card'}</DialogTitle>
          <DialogDescription>
            {card ? 'Update card information' : 'Add a new card to the game'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="card-name-input"
              />
            </div>
            <div>
              <Label htmlFor="movie_source">Movie Source</Label>
              <Input
                id="movie_source"
                value={formData.movie_source}
                onChange={(e) => setFormData({ ...formData, movie_source: e.target.value })}
                required
                data-testid="card-movie-input"
              />
            </div>
            <div>
              <Label htmlFor="cost">Cost (0-6)</Label>
              <Input
                id="cost"
                type="number"
                min="0"
                max="6"
                value={formData.cost}
                onChange={(e) => setFormData({ ...formData, cost: parseInt(e.target.value) })}
                required
                data-testid="card-cost-input"
              />
            </div>
            <div>
              <Label htmlFor="power">Power</Label>
              <Input
                id="power"
                type="number"
                value={formData.power}
                onChange={(e) => setFormData({ ...formData, power: parseInt(e.target.value) })}
                required
                data-testid="card-power-input"
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="ability_type">Ability Type</Label>
            <Select
              value={formData.ability_type}
              onValueChange={(value) => setFormData({ ...formData, ability_type: value })}
            >
              <SelectTrigger data-testid="card-ability-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="on_reveal">On Reveal</SelectItem>
                <SelectItem value="ongoing">Ongoing</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="ability_description">Ability Description</Label>
            <Textarea
              id="ability_description"
              value={formData.ability_description}
              onChange={(e) => setFormData({ ...formData, ability_description: e.target.value })}
              rows={3}
              data-testid="card-ability-desc-input"
            />
          </div>

          {card && (
            <div>
              <Label htmlFor="image">Card Image</Label>
              <div className="flex items-center gap-4">
                <Input
                  id="image"
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploading}
                  data-testid="card-image-upload"
                />
                {formData.image_url && (
                  <img src={formData.image_url} alt="Card preview" className="h-16 w-16 object-cover rounded" />
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" data-testid="save-card-btn">{card ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Location Form Dialog Component
const LocationFormDialog = ({ open, onOpenChange, location, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    effect_type: 'power_boost',
    effect_description: '',
    effect_value: null
  });
  const { toast } = useToast();

  useEffect(() => {
    if (location) {
      setFormData(location);
    } else {
      setFormData({
        name: '',
        effect_type: 'power_boost',
        effect_description: '',
        effect_value: null
      });
    }
  }, [location, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (location) {
        await axios.put(`${API}/locations/${location.id}`, formData);
        toast({ title: 'Success', description: 'Location updated successfully' });
      } else {
        await axios.post(`${API}/locations`, formData);
        toast({ title: 'Success', description: 'Location created successfully' });
      }
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save location', variant: 'destructive' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="location-form-dialog">
        <DialogHeader>
          <DialogTitle>{location ? 'Edit Location' : 'Create Location'}</DialogTitle>
          <DialogDescription>
            {location ? 'Update location information' : 'Add a new location to the game'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="loc-name">Name</Label>
            <Input
              id="loc-name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              data-testid="location-name-input"
            />
          </div>
          
          <div>
            <Label htmlFor="effect_type">Effect Type</Label>
            <Select
              value={formData.effect_type}
              onValueChange={(value) => setFormData({ ...formData, effect_type: value })}
            >
              <SelectTrigger data-testid="location-effect-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="power_multiplier">Power Multiplier</SelectItem>
                <SelectItem value="cost_restriction">Cost Restriction</SelectItem>
                <SelectItem value="card_limit">Card Limit</SelectItem>
                <SelectItem value="power_boost">Power Boost</SelectItem>
                <SelectItem value="special">Special</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="effect_description">Effect Description</Label>
            <Textarea
              id="effect_description"
              value={formData.effect_description}
              onChange={(e) => setFormData({ ...formData, effect_description: e.target.value })}
              rows={3}
              required
              data-testid="location-effect-desc-input"
            />
          </div>

          <div>
            <Label htmlFor="effect_value">Effect Value (Optional)</Label>
            <Input
              id="effect_value"
              type="number"
              value={formData.effect_value ?? ''}
              onChange={(e) => setFormData({ ...formData, effect_value: e.target.value ? parseInt(e.target.value) : null })}
              data-testid="location-effect-value-input"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" data-testid="save-location-btn">{location ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AdminPanel;